import { type RefObject, useCallback, useEffect, useState } from 'react';

import { createResizeObserverWithRAF } from '@heidi/utils';

export interface VerticalScrollFadeState {
  canScrollUp: boolean;
  canScrollDown: boolean;
}

/**
 * Tracks vertical scroll position of a scrollable container so callers can
 * drive top/bottom fade gradients. Re-reads when `enabled` flips (e.g. a
 * collapsible expanding), when any dep in `recomputeDeps` changes, or when the
 * container or its first child resizes.
 */
export const useVerticalScrollFade = (
  scrollRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  recomputeDeps: ReadonlyArray<unknown> = []
): VerticalScrollFadeState => {
  const [state, setState] = useState<VerticalScrollFadeState>({
    canScrollUp: false,
    canScrollDown: false,
  });

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const canScrollUp = el.scrollTop > 0;
    const canScrollDown = el.scrollTop < el.scrollHeight - el.clientHeight - 1;

    setState((prev) =>
      prev.canScrollUp === canScrollUp && prev.canScrollDown === canScrollDown
        ? prev
        : { canScrollUp, canScrollDown }
    );
  }, [scrollRef]);

  useEffect(() => {
    if (enabled) {
      requestAnimationFrame(update);
    }
    // recomputeDeps is intentionally spread so callers can re-check on arbitrary changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, update, ...recomputeDeps]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', update);
    return () => el.removeEventListener('scroll', update);
  }, [scrollRef, update]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!enabled || !el || typeof ResizeObserver === 'undefined') return;

    // The container's own box rarely changes when content loads after mount, so the
    // content wrapper (e.g. a Radix ScrollArea viewport's child) is observed too.
    const observer = createResizeObserverWithRAF(update);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [enabled, scrollRef, update]);

  return state;
};
